/**
 * Age + customer-type helpers for RAO.
 *
 * Port of legacy logic that lived in three places:
 *   - screens/ProductsScreen.tsx:16-30 (`calcAge`)
 *   - components/ownership/BeneficiarySection.tsx:16-25
 *   - components/people/PersonSearchBar.tsx:212-234 (minor/business auto-detect)
 *
 * Centralized here per feedback_port_not_reinvent + G-RAO-05.
 */

/**
 * Compute age at today from a YYYY-MM-DD birthdate string. Returns null for
 * invalid / missing input. Splits the string by hand to avoid the UTC-parse
 * off-by-one bug flagged in G-RAO-20.
 */
export function ageFromDob(dob: string | null | undefined): number | null {
  if (!dob) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dob);
  if (!m) return null;
  const y = Number.parseInt(m[1]!, 10);
  const mo = Number.parseInt(m[2]!, 10);
  const d = Number.parseInt(m[3]!, 10);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;
  const now = new Date();
  let age = now.getFullYear() - y;
  const curMonth = now.getMonth() + 1;
  const curDay = now.getDate();
  if (curMonth < mo || (curMonth === mo && curDay < d)) age -= 1;
  return age >= 0 ? age : null;
}

/**
 * Auto-switch a captured Person's `customerType` to `Minor` if the DOB puts
 * them under 18 (Individual -> Minor only). Returns the corrected customer
 * type or the original if no change applies.
 *
 * Legacy reference: PersonSearchBar.tsx:212-234.
 */
export function autoDetectMinor<T extends string>(
  currentType: T | undefined,
  dob: string | undefined,
): T | 'Minor' | undefined {
  if (currentType !== 'Individual') return currentType;
  const age = ageFromDob(dob);
  if (age === null) return currentType;
  return age < 18 ? 'Minor' : currentType;
}

/**
 * Convenience: is this customer-type representative of a business (any B2B
 * entity)? Drives BO matrix, employer read-only rules, etc.
 */
export function isBusinessType(t: string | undefined): boolean {
  if (!t) return false;
  switch (t) {
    case 'Corporation':
    case 'LLC':
    case 'DBA':
    case 'SoleProp':
    case 'Partnership':
    case 'LLP':
    case 'LLLP':
    case 'LimitedPartnership':
    case 'GeneralPartnership':
    case 'NonProfit':
    case 'Association':
    case 'Church':
    case 'PublicFund':
    case 'Government':
    case 'SchoolDistrict':
      return true;
    default:
      return false;
  }
}
